import { useSpaceIDFromPath } from '@/data/space/hooks';
import {
  useGetProductCollectionQuery,
  useGetSpaceQuery,
  useUpdateSenderNameMutation,
} from '@/store';
import { errToString } from '@typedream/data';
import { Button, Text } from '@typedream/ui';
import classNames from 'classnames';
import { FormEventHandler, useEffect, useState } from 'react';
import sharedStyles from '../../styles/Shared.module.css';

export function ProductsEmailSender() {
  const [senderName, setSenderName] = useState('');
  const [senderNameError, setSenderNameError] = useState(undefined);
  const spaceID = useSpaceIDFromPath();
  const { data: space, isLoading: loadingSpace } = useGetSpaceQuery([spaceID]);
  const { data: productCollection, isLoading: loadingProductCollection } =
    useGetProductCollectionQuery([spaceID]);
  const {
    mutate: updateSenderName,
    isLoading: isUpdatingSenderName,
    error,
  } = useUpdateSenderNameMutation();

  const loading = loadingSpace || loadingProductCollection;
  const disabled =
    !senderName ||
    senderName.trim() === (productCollection?.field.mailing?.sender_name || space?.name || '');

  useEffect(() => {
    if (productCollection?.field.mailing?.sender_name) {
      setSenderName(productCollection.field.mailing.sender_name);
    } else if (space?.name) {
      setSenderName(space.name);
    }
  }, [productCollection, space]);

  const onSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (!senderName) {
      setSenderNameError("Email sender name can't be empty");
      return;
    }

    await updateSenderName({ spaceID, senderName });
  };

  const resetSenderName = () => {
    setSenderNameError(undefined);
    setSenderName(productCollection?.field.mailing?.sender_name || space?.name || '');
  };

  return (
    <form className="flex flex-col items-start gap-4" onSubmit={onSubmit}>
      <div className="flex flex-col gap-2.5">
        <Text as="h2" size="md" className="tracking-[-0.01em]">
          Email Sender Name
        </Text>
        <Text as="p" size="sm" weight="medium" className="text-secondary">
          This will be sender name displayed on email receipts sent after a purchase of a free or
          paid products.
        </Text>
      </div>
      <input
        className={classNames(sharedStyles.inputMedium, {
          [sharedStyles.inputError]: !!error || !!senderNameError,
        })}
        type="text"
        value={senderName}
        disabled={loading}
        onChange={(e) => setSenderName(e.target.value)}
      />
      {senderNameError ||
        (error && (
          <Text size="xs" className="text-danger">
            {senderNameError || errToString(error)}
          </Text>
        ))}
      <div className="flex items-center gap-2">
        <Button type="submit" isLoading={isUpdatingSenderName} isDisabled={loading || disabled}>
          Update Sender Name
        </Button>
        {!(loading || disabled) && (
          <Button variant="ghost" onClick={resetSenderName}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
